/**
 * Toolbar do MPA (GUPM-193): 5 pills globais (padrão do `TarpSummaryCard` — número
 * em cima, rótulo embaixo) + 5 filtros (Barragem/Status/Nível/Prazo/Responsável),
 * fiéis ao V8. As contagens das pills são GLOBAIS (não-filtradas). Componente burro:
 * recebe `contagem`/`filtros`/`responsaveis` e emite `onChange`.
 */
import type { ContagemGlobal, MpaFiltros } from '@/domain/planos/mpa';

const PILLS: { key: keyof ContagemGlobal; label: string; color: string }[] = [
  { key: 'total', label: 'Total', color: '#94a3b8' },
  { key: 'pendentes', label: 'Pendentes', color: '#f97316' },
  { key: 'andamento', label: 'Em andamento', color: '#78B4CF' },
  { key: 'concluidas', label: 'Concluídas', color: '#22c55e' },
  { key: 'vencidas', label: 'Vencidas', color: '#ef4444' },
];

/** Um chip de filtro. O rótulo é o accessible-name do botão (usado nos testes). */
function Chip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`rounded-full border px-2.5 py-1 text-[11px] font-bold transition ${
        active
          ? 'border-jf-yellow bg-jf-yellow text-jf-bg'
          : 'border-jf-border bg-jf-surface2/40 text-jf-muted hover:text-white'
      }`}
    >
      {label}
    </button>
  );
}

/**
 * Um grupo de filtro (rótulo + opções). É ATÔMICO: `shrink-0` + chips em linha única
 * (`flex` sem wrap) garantem que as opções de um mesmo filtro nunca se partem entre
 * linhas. Os grupos fluem num container `flex-wrap`, então quebram para novas linhas
 * quando a tela encolhe e voltam lado a lado quando ela cresce. O `border-l` é o
 * divisor entre grupos vizinhos.
 */
function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-l border-jf-border pl-3">
      <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-wider text-jf-dim">{label}</span>
      <div className="flex gap-1.5">{children}</div>
    </div>
  );
}

// Rótulos SINGULARES de propósito: os plurais ("Pendentes"/"Em andamento"/
// "Concluídas") são exclusivos das pills globais (asserções de teto por getByText).
const STATUS_OPTS: [MpaFiltros['status'], string][] = [
  ['Todas', 'Todos'],
  ['pendente', 'Pendente'],
  ['andamento', 'Andamento'],
  ['concluido', 'Concluído'],
];
const NIVEL_OPTS: [MpaFiltros['nivel'], string][] = [
  ['Todos', 'Todos'],
  ['ATENCAO', 'Atenção'],
  ['ALERTA', 'Alerta'],
  ['EMERGENCIA', 'Emergência'],
];
const PRAZO_OPTS: MpaFiltros['prazo'][] = ['Todos', 'Vencido', 'Esta semana', 'Sem prazo'];

interface Props {
  contagem: ContagemGlobal;
  filtros: MpaFiltros;
  responsaveis: string[];
  onChange: (f: MpaFiltros) => void;
}

export function MpaToolbar({ contagem, filtros, responsaveis, onChange }: Props) {
  const set = (patch: Partial<MpaFiltros>) => onChange({ ...filtros, ...patch });
  return (
    <div className="mb-3 space-y-3">
      <div className="flex flex-wrap gap-2">
        {PILLS.map((p) => (
          <div
            key={p.key}
            className="min-w-[64px] flex-1 rounded-[10px] border px-1.5 py-2.5 text-center"
            style={{ background: `${p.color}14`, borderColor: `${p.color}55` }}
          >
            <div className="text-xl font-black leading-none" style={{ color: p.color }}>
              {contagem[p.key]}
            </div>
            <div className="mt-0.5 text-[10px] font-semibold leading-tight text-jf-muted">{p.label}</div>
          </div>
        ))}
      </div>

      {/*
        Barra de filtros fluida: os grupos ficam lado a lado e preenchem a linha; quando a
        tela encolhe, quebram para linhas novas (flex-wrap) sem nunca partir as opções de um
        mesmo filtro. Cada grupo tem um divisor à esquerda (border-l) — ele apareceria solto
        no início de cada linha quebrada, então o `-ml-[13px]` (px-3 do card + 1px do divisor)
        empurra esse primeiro divisor para dentro do padding, onde o `overflow-hidden` recorta.
      */}
      <div className="overflow-hidden rounded-xl border border-jf-border bg-jf-surface px-3 py-2.5">
        <div className="-ml-[13px] flex flex-wrap items-center gap-x-3 gap-y-2">
          <FilterGroup label="Barragem">
            {(['Todas', 'A', 'B', 'C'] as const).map((d) => (
              <Chip key={d} active={filtros.dam === d} label={d} onClick={() => set({ dam: d })} />
            ))}
          </FilterGroup>
          <FilterGroup label="Status">
            {STATUS_OPTS.map(([v, l]) => (
              <Chip key={v} active={filtros.status === v} label={l} onClick={() => set({ status: v })} />
            ))}
          </FilterGroup>
          <FilterGroup label="Nível">
            {NIVEL_OPTS.map(([v, l]) => (
              <Chip key={v} active={filtros.nivel === v} label={l} onClick={() => set({ nivel: v })} />
            ))}
          </FilterGroup>
          <FilterGroup label="Prazo">
            {PRAZO_OPTS.map((v) => (
              <Chip key={v} active={filtros.prazo === v} label={v} onClick={() => set({ prazo: v })} />
            ))}
          </FilterGroup>
          <FilterGroup label="Responsável">
            <select
              aria-label="Filtrar por responsável"
              value={filtros.resp}
              onChange={(e) => set({ resp: e.target.value })}
              className="rounded-lg border border-jf-border bg-jf-surface2/40 px-2 py-1 text-[11px] font-semibold text-white"
            >
              <option value="Todos">Todos</option>
              {responsaveis.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </FilterGroup>
        </div>
      </div>
    </div>
  );
}
